import React from 'react';
import PropTypes from 'prop-types';
import { LuUserCheck, LuUserX, LuUsers } from 'react-icons/lu';
import GlassIcon from '../../../components/ui/GlassIcon';
import { useTranslation } from '../../../i18n/LanguageContext';

const StatTile = ({ label, shortLabel, value, share, icon: Icon, gradient, barClass, ready, locale, shareTitle }) => (
  <div className="tw-flex tw-min-w-0 tw-flex-col tw-gap-3 tw-rounded-[1.4rem] tw-border tw-border-white/80 tw-bg-white/70 tw-p-3.5 tw-shadow-[inset_0_1px_0_rgba(255,255,255,1),0_12px_32px_-24px_rgba(15,23,42,0.4)] sm:tw-p-5 [@media(max-height:820px)]:tw-py-3 dark:tw-border-white/10 dark:tw-bg-[#22262f]/80 dark:tw-shadow-[inset_0_1px_0_rgba(255,255,255,0.06),0_12px_32px_-24px_rgba(0,0,0,0.9)]">
    <div className="tw-flex tw-items-center tw-gap-3">
      <GlassIcon gradient={gradient} size="xs" className="tw-hidden sm:tw-grid">
        <Icon aria-hidden="true" className="tw-h-[18px] tw-w-[18px]" />
      </GlassIcon>
      <div className="tw-min-w-0">
        {ready ? (
          <p className="tw-text-2xl tw-font-semibold tw-leading-none tw-tracking-tight tw-text-slate-900 tw-tabular-nums sm:tw-text-3xl dark:tw-text-white">
            {value.toLocaleString(locale)}
          </p>
        ) : (
          <span aria-hidden="true" className="tw-block tw-h-7 tw-w-14 tw-animate-pulse tw-rounded-lg tw-bg-slate-200/80 sm:tw-h-8 dark:tw-bg-white/10" />
        )}
        <p className="tw-mt-1.5 tw-truncate tw-text-xs tw-font-medium tw-text-slate-500 sm:tw-text-sm dark:tw-text-slate-400">
          {/* Phones show the short form; three tiles share the width there */}
          <span className="sm:tw-hidden">{shortLabel}</span>
          <span className="tw-hidden sm:tw-inline">{label}</span>
        </p>
      </div>
    </div>

    {share !== undefined && (
      <div className="tw-flex tw-items-center tw-gap-2 [@media(max-height:820px)]:tw-hidden" title={shareTitle}>
        <span className="tw-h-1.5 tw-flex-1 tw-overflow-hidden tw-rounded-full tw-bg-slate-200/80 dark:tw-bg-white/10">
          <span
            className={`tw-block tw-h-full tw-rounded-full tw-transition-[width] tw-duration-700 tw-ease-out ${barClass}`}
            style={{ width: `${Math.round(share * 100)}%` }}
          />
        </span>
        <span className="tw-hidden tw-text-xs tw-font-semibold tw-text-slate-500 tw-tabular-nums sm:tw-inline dark:tw-text-slate-400">
          {new Intl.NumberFormat(locale, { style: 'percent' }).format(share)}
        </span>
      </div>
    )}
  </div>
);

StatTile.propTypes = {
  label: PropTypes.string.isRequired,
  shortLabel: PropTypes.string.isRequired,
  value: PropTypes.number.isRequired,
  share: PropTypes.number,
  icon: PropTypes.elementType.isRequired,
  gradient: PropTypes.string.isRequired,
  barClass: PropTypes.string,
  ready: PropTypes.bool,
  locale: PropTypes.string,
  shareTitle: PropTypes.string,
};

/** Total / active / inactive head counts. */
const EmployeeStats = ({ total, active, inactive, ready }) => {
  const { t, locale } = useTranslation();
  const shareOf = (count) => (ready && total > 0 ? Math.min(1, count / total) : 0);
  const shareTitle = (share) => t('employees.stats.share', { percent: Math.round(share * 100) });

  return (
    <div className="tw-grid tw-grid-cols-3 tw-gap-2.5 sm:tw-gap-4">
      <StatTile
        label={t('employees.stats.total')}
        shortLabel={t('employees.stats.totalShort')}
        locale={locale}
        value={total}
        icon={LuUsers}
        gradient="tw-from-sky-400 tw-to-blue-600 tw-shadow-blue-500/30"
        ready={ready}
      />
      <StatTile
        label={t('employees.stats.active')}
        shortLabel={t('employees.stats.activeShort')}
        value={active}
        share={shareOf(active)}
        shareTitle={shareTitle(shareOf(active))}
        locale={locale}
        icon={LuUserCheck}
        gradient="tw-from-emerald-400 tw-to-teal-600 tw-shadow-emerald-500/30"
        barClass="tw-bg-gradient-to-r tw-from-emerald-400 tw-to-teal-500"
        ready={ready}
      />
      <StatTile
        label={t('employees.stats.inactive')}
        shortLabel={t('employees.stats.inactiveShort')}
        value={inactive}
        share={shareOf(inactive)}
        shareTitle={shareTitle(shareOf(inactive))}
        locale={locale}
        icon={LuUserX}
        gradient="tw-from-rose-400 tw-to-pink-600 tw-shadow-rose-500/30"
        barClass="tw-bg-gradient-to-r tw-from-rose-400 tw-to-pink-500"
        ready={ready}
      />
    </div>
  );
};

EmployeeStats.propTypes = {
  total: PropTypes.number.isRequired,
  active: PropTypes.number.isRequired,
  inactive: PropTypes.number.isRequired,
  ready: PropTypes.bool,
};

export default EmployeeStats;
